import React, { useEffect, useState } from "react";
import { Sparkles, X, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const DISMISS_KEY = "bs_trade_nudge_dismissed";

export const TradePackNudge = () => {
  const { tenant, refreshMe, user } = useAuth();
  const [packs, setPacks] = useState([]);
  const [pack, setPack] = useState("");
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(() => { try { return localStorage.getItem(DISMISS_KEY) === "1"; } catch { return false; } });

  const canWrite = user?.role === "owner" || user?.role === "admin";
  const show = canWrite && tenant && !tenant.trade && !dismissed;

  useEffect(() => {
    if (!show || packs.length) return;
    api.get(`/rates/starter-packs`).then(({ data }) => {
      const list = (data.packs || []).filter((p) => p.key !== "general");
      setPacks(list);
      if (list.length) setPack(list[0].key);
    }).catch(() => {});
  }, [show, packs.length]);

  if (!show) return null;

  const dismiss = () => { try { localStorage.setItem(DISMISS_KEY, "1"); } catch {} setDismissed(true); };

  const load = async () => {
    if (!pack) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/rates/seed-starter`, { pack });
      await api.put(`/settings/trade`, { trade: pack });
      await refreshMe();
      const extra = data.skipped ? ` (${data.skipped} already saved)` : "";
      toast.success(`Added ${data.added} ${data.label} rates to your library${extra}. Now "Add & price with AI" works instantly.`);
      dismiss();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="mb-6 rounded-xl border border-blue-200 bg-blue-50 p-5" data-testid="trade-pack-nudge">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-lg bg-blue-600/10 flex items-center justify-center shrink-0">
          <Sparkles className="h-5 w-5 text-blue-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-display font-semibold text-slate-900 text-sm">Load your trade's rate library</p>
          <p className="text-sm text-slate-600 mt-0.5">Pick your main trade and we'll drop in a ready-made set of UK rates — so pricing a takeoff works from day one.</p>
          <div className="mt-3 flex flex-col sm:flex-row gap-2 sm:items-center">
            <select
              value={pack}
              onChange={(e) => setPack(e.target.value)}
              data-testid="trade-nudge-select"
              className="h-9 border border-slate-300 rounded-md text-sm px-2 bg-white sm:w-64"
            >
              {packs.map((p) => <option key={p.key} value={p.key}>{p.label} ({p.count} rates)</option>)}
            </select>
            <Button size="sm" onClick={load} disabled={busy || !pack} data-testid="trade-nudge-load" className="bg-blue-600 hover:bg-blue-700 h-9">
              {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />} Load these rates
            </Button>
          </div>
        </div>
        <button onClick={dismiss} data-testid="trade-nudge-dismiss" className="text-slate-400 hover:text-slate-600 shrink-0" aria-label="Dismiss">
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default TradePackNudge;
